/**
 * PaymentHistoryCard — updated to match mock design.
 *
 * Status indicators:
 *  - Green  ✓  for PAID
 *  - Blue   ✓  + "JUST PAID" tag when installment.paymentId === justPaidPaymentId
 *  - Orange →  (SOON) for the next upcoming installment
 *  - Grey   ·  for remaining PENDING installments
 *
 * Adjusted amounts (via shared helpers — same rules as computePaymentSummary / refine):
 *  - Paid rows: stamped transactionIds (fallback transactionId)
 *  - First pending installment: unused PENDING adjustments only
 *    (excludes IDs already stamped — avoids double-count while POSTED lag)
 */
import React from 'react';
import { formatDateTime, netAdjustmentAmount, firstPendingInstallment, unusedPendingAdjustments, lookupTxnsByStamp } from '../../utils/subscriptionHelpers';
import { formatDate } from '../../utils/dateFormatter';
import { AdjustedAmount } from './AdjustedAmount';
import type {
  ScheduledInstallment,
  IssuedToken,
  Transaction,
  InstallmentWithTxn,
  IssuedTokenWithTxn,
} from '../../api/payment';
import './styles/PaymentHistoryCard.css';

export interface PaymentHistoryCardProps {
  token?: IssuedToken;
  installments: ScheduledInstallment[];
  /** All transactions (PENDING + POSTED) for this subscription */
  transactions?: Transaction[];
  /** Highlights the just-paid row (Success page only) */
  justPaidPaymentId?: string;
  title?: string;
}

export const PaymentHistoryCard: React.FC<PaymentHistoryCardProps> = ({
  token,
  installments,
  transactions = [],
  justPaidPaymentId,
  title = 'Payment History',
}) => {
  const hasToken = token && token.amount && token.amount > 0;
  if (!hasToken && installments.length === 0) return null;

  const nextDueInstallment = firstPendingInstallment(installments);
  const allTxnsById = new Map(transactions.map(t => [t.id, t]));
  const pendingTxns = unusedPendingAdjustments(transactions, token, installments);

  /* ─── helpers ─────────────────────────────────────────── */

  /** Resolve indicator colour variant for an installment row */
  const indicatorVariant = (
    isPaid: boolean,
    isJustPaid: boolean,
    isNextDue: boolean,
  ): 'green' | 'blue' | 'orange' | 'grey' => {
    if (isJustPaid) return 'blue';
    if (isPaid) return 'green';
    if (isNextDue) return 'orange';
    return 'grey';
  };

  const indicatorIcon = (variant: string, isPaid: boolean, isJustPaid: boolean) => {
    if (isPaid || isJustPaid) return '✓';
    if (variant === 'orange') return '→';
    return '·';
  };

  return (
    <div className="ph">
      <div className="ph__card">
        <h3 className="ph__section-title">{title}</h3>

        {hasToken && (() => {
          const isJustPaid = token.paymentId === justPaidPaymentId;
          const isPaid = isJustPaid || !!token.paidAt;
          const variant = isJustPaid ? 'blue' : isPaid ? 'green' : 'orange';
          const icon = isPaid ? '✓' : '→';

          // Paid token: show POSTED transaction matched by transactionId on the token
          // Unpaid token (no pending installment exists): show PENDING adjustments
          const tokenTxns = isPaid
            ? lookupTxnsByStamp(token as IssuedTokenWithTxn, allTxnsById)
            : (nextDueInstallment == null ? pendingTxns : []);
          const tokenAdj = netAdjustmentAmount(tokenTxns);

          return (
            <div className={`ph__row${isJustPaid ? ' ph__row--highlight' : ''}`}>
              <span className={`ph__indicator ph__indicator--${variant}`}>{icon}</span>
              <div className="ph__body">
                <span className={`ph__label${!isPaid ? ' ph__label--orange' : ''}`}>
                  Token Amount
                </span>
                <span className="ph__date">
                  {isPaid
                    ? `Paid on ${formatDateTime(token.paidAt)}`
                    : 'Payment pending'}
                </span>
              </div>
              <AdjustedAmount
                original={token.amount ?? 0}
                adj={tokenAdj}
                originalClassName={`ph__amount${!isPaid ? ' ph__amount--orange' : ''}`}
              />
            </div>
          );
        })()}

        {installments.map(inst => {
          const isJustPaid = inst.paymentId === justPaidPaymentId;
          const isPaid = inst.status === 'PAID' || !!inst.paidAt;
          const isNextDue = !isPaid && !isJustPaid && inst === nextDueInstallment;

          const variant = indicatorVariant(isPaid, isJustPaid, isNextDue);
          const icon = indicatorIcon(variant, isPaid, isJustPaid);

          const instTxns = isPaid
            ? lookupTxnsByStamp(inst as InstallmentWithTxn, allTxnsById)
            : (inst === nextDueInstallment ? pendingTxns : []);
          const instAdj = netAdjustmentAmount(instTxns);

          return (
            <div
              key={inst.installmentNumber}
              className={`ph__row${isJustPaid ? ' ph__row--highlight' : ''}`}
            >
              <span className={`ph__indicator ph__indicator--${variant}`}>{icon}</span>

              <div className="ph__body">
                <span className={`ph__label${isNextDue ? ' ph__label--orange' : ''}`}>
                  Installment {inst.installmentNumber}
                </span>
                <span className="ph__date">
                  {isPaid
                    ? `Paid on ${formatDateTime(inst.paidAt)}`
                    : inst.dueDate
                      ? `Due ${formatDate(inst.dueDate)}`
                      : '—'}
                </span>
              </div>

              <AdjustedAmount
                original={inst.amount ?? 0}
                adj={instAdj}
                originalClassName={`ph__amount${
                    isNextDue
                      ? ' ph__amount--orange'
                      : !isPaid && !isJustPaid && !isNextDue
                        ? ' ph__amount--grey'
                        : ''
                  }`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentHistoryCard;
